import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  MessageSquareQuote,
  FileText,
  Mail,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ListChecks,
  ClipboardList,
} from 'lucide-react';

const AdminLayout = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Force LTR for admin regardless of site language setting
    document.documentElement.dir = 'ltr';
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
    { icon: ClipboardList, label: 'Kitchen Orders', path: '/admin/orders' },
    { icon: FolderKanban, label: 'Projects', path: '/admin/projects' },
    { icon: MessageSquareQuote, label: 'Testimonials', path: '/admin/testimonials' },
    { icon: FileText, label: 'Blog', path: '/admin/blog' },
    { icon: ListChecks, label: 'Process Steps', path: '/admin/process-steps' },
    { icon: Mail, label: 'Contacts', path: '/admin/contacts' },
    { icon: Settings, label: 'Settings', path: '/admin/settings' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-charcoal-deep" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-brand-panel border-b border-white/10 flex items-center justify-between px-4 z-50">
        <span className="text-white font-bold">ARMAN Admin</span>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-brand-dark border-r border-white/10 z-40 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <span className="text-2xl font-bold text-white">ARMAN</span>
            <span className="text-gold text-sm">Admin</span>
          </Link>

          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  location.pathname === item.path
                    ? 'bg-gold text-charcoal'
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center text-gold font-bold">
              {user.name?.charAt(0) || 'A'}
            </div>
            <div>
              <p className="text-white text-sm font-medium">{user.name}</p>
              <p className="text-white/40 text-xs">{user.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout"
            className="flex items-center gap-2 text-white/60 hover:text-red-400 transition-colors"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
